import { useEffect, useState } from "react";

import { DEFAULT_SERVICE_DATE_TIMEZONE } from "../constants/service-date-timezone";
import { UsePracticeTimezoneOptions } from "../interfaces/service-date-timezone";
import { getBrowserServiceDateTimezone } from "../utils/browser-timezone";
import { isValidTimezone } from "../utils/is-valid-timezone";
import {
  addCalendarDays,
  getPracticeCalendarDate,
  getStartOfPracticeCalendarDate,
} from "../utils/practice-calendar-date";

import { usePracticeTimezone } from "./usePracticeTimezone";

/** Delay past practice midnight before re-reading the date, so the timer never lands on the old day. */
const DAY_ROLLOVER_BUFFER_MS = 1000;

export interface PracticeToday {
  /** Practice-local calendar date (`yyyy-MM-dd`). */
  today: string;
  /** IANA timezone `today` is read in. */
  timeZone: string;
  /** True until the practice timezone has loaded; `today` uses a fallback timezone until then. */
  isLoading: boolean;
  /** True when the timezone lookup failed; `today` is then read in the browser timezone. */
  hasError: boolean;
}

interface TrackedDay {
  timeZone: string;
  today: string;
}

/**
 * Tracks today's calendar date in the practice timezone.
 *
 * Uses the practice's location-hours timezone, or the practice default when `usePracticeDefault` is
 * set. Falls back to the browser timezone when the practice
 * does not configure exactly one, the lookup fails, or the code is not a recognized timezone.
 * Updates at practice-local midnight and whenever the tab becomes visible or focused again, so a
 * page left open overnight (or through a laptop sleep) moves to the new day.
 */
export function usePracticeToday(options: UsePracticeTimezoneOptions = {}): PracticeToday {
  const { timezone, isLoading, hasError } = usePracticeTimezone(options);
  const timeZone = resolveTimeZone(timezone.code, hasError);
  const [trackedDay, setTrackedDay] = useState<TrackedDay>(() => ({
    timeZone,
    today: getPracticeCalendarDate(new Date(), timeZone),
  }));
  // On a timezone change, read today in the new zone during this render, so callers never get a
  // date read in the previous zone paired with the new one.
  const today =
    trackedDay.timeZone === timeZone
      ? trackedDay.today
      : getPracticeCalendarDate(new Date(), timeZone);

  useEffect(() => {
    const syncToday = () => {
      const currentDay = getPracticeCalendarDate(new Date(), timeZone);
      setTrackedDay((tracked) =>
        tracked.timeZone === timeZone && tracked.today === currentDay
          ? tracked
          : { timeZone, today: currentDay },
      );
    };
    syncToday();

    const nextDayStart = getStartOfPracticeCalendarDate(addCalendarDays(today, 1), timeZone);
    const timeoutId = window.setTimeout(
      syncToday,
      Math.max(nextDayStart.getTime() - Date.now(), 0) + DAY_ROLLOVER_BUFFER_MS,
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") syncToday();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", syncToday);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", syncToday);
    };
  }, [timeZone, today]);

  return { today, timeZone, isLoading, hasError };
}

function resolveTimeZone(timezoneCode: string | undefined, hasError: boolean): string {
  if (!hasError && timezoneCode && isValidTimezone(timezoneCode)) return timezoneCode;
  return getBrowserServiceDateTimezone().code ?? DEFAULT_SERVICE_DATE_TIMEZONE.code;
}
